import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext.jsx';
import api from '../services/api.js';
import { formatAmount, setDisplayCurrency } from '../utils/money.js';

/** Window event fired after a configuration save so every mounted portal re-applies branding immediately. */
export const BRANDING_UPDATED_EVENT = 'tenant-branding-updated';
const POLL_MS = 30000;

const DEFAULTS = {
  version: 0,
  orgName: null,
  primaryColor: null,
  secondaryColor: null,
  highlightColor: null,
  logoUrl: null,
  faviconUrl: null,
  timezone: 'Asia/Dubai',
  currency: 'AED',
  dateFormat: 'YYYY-MM-DD',
};

const TenantBrandingContext = createContext({ branding: DEFAULTS, logoSrc: null, formatDate: (v) => String(v ?? ''), formatCurrency: (v) => String(v ?? ''), refresh: () => {} });

// eslint-disable-next-line react-refresh/only-export-components
export const useTenantBranding = () => useContext(TenantBrandingContext);

function shade(hex, amount) {
  const n = parseInt(hex.slice(1), 16);
  const ch = (shift) => Math.max(0, Math.min(255, Math.round(((n >> shift) & 255) * (1 + amount))));
  return `#${[16, 8, 0].map((s) => ch(s).toString(16).padStart(2, '0')).join('')}`;
}
const rgb = (hex) => `${parseInt(hex.slice(1, 3), 16)}, ${parseInt(hex.slice(3, 5), 16)}, ${parseInt(hex.slice(5, 7), 16)}`;

/** Theme overrides: CSS variables plus the Bootstrap primary button / link / badge styles. */
// eslint-disable-next-line react-refresh/only-export-components
export function buildBrandingCss(b) {
  if (!b.primaryColor) return '';
  const p = b.primaryColor;
  const h = b.highlightColor || p;
  return `
:root{--tenant-primary:${p};--tenant-secondary:${b.secondaryColor};--tenant-highlight:${h};--color-secondary:${p};--color-accent:${h};--secondary-color:${p};--accent-color:${h};--bs-primary:${p};--bs-primary-rgb:${rgb(p)};--bs-link-color:${p};--bs-link-hover-color:${shade(p, -0.2)};--bs-link-color-rgb:${rgb(p)}}
.btn-primary{--bs-btn-bg:${p};--bs-btn-border-color:${p};--bs-btn-hover-bg:${shade(p, -0.12)};--bs-btn-hover-border-color:${shade(p, -0.16)};--bs-btn-active-bg:${shade(p, -0.2)};--bs-btn-active-border-color:${shade(p, -0.24)};--bs-btn-disabled-bg:${p};--bs-btn-disabled-border-color:${p}}
.btn-outline-primary{--bs-btn-color:${p};--bs-btn-border-color:${p};--bs-btn-hover-bg:${p};--bs-btn-hover-border-color:${p};--bs-btn-active-bg:${p};--bs-btn-active-border-color:${p}}
.text-primary{color:${p} !important}.bg-primary,.text-bg-primary{background-color:${p} !important}.border-primary{border-color:${p} !important}
.badge.text-bg-info,.badge.bg-info{background-color:${h} !important}
.app-top-navbar{background-color:${b.secondaryColor} !important}`;
}

export function TenantBrandingProvider({ children }) {
  const { user } = useAuth();
  const [branding, setBranding] = useState(DEFAULTS);
  const [logoSrc, setLogoSrc] = useState(null);
  const objectUrl = useRef(null);
  const versionRef = useRef(null);
  const userId = user?.id ?? user?.sub ?? null;

  const refresh = useCallback(async () => {
    if (!userId) return;
    try {
      const next = await api.getTenantBranding();
      const key = `${next.version}|${next.logoUrl}`;
      if (versionRef.current === key) return;
      versionRef.current = key;
      setBranding(next);
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
      objectUrl.current = null;
      if (next.logoUrl) {
        try {
          objectUrl.current = URL.createObjectURL(await api.fetchTenantAssetBlob('logo'));
        } catch {
          /* logo is optional chrome */
        }
      }
      setLogoSrc(objectUrl.current);
      if (next.faviconUrl) {
        try {
          const href = URL.createObjectURL(await api.fetchTenantAssetBlob('favicon'));
          let link = document.querySelector('link[rel~="icon"]');
          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
          }
          link.href = href;
        } catch {
          /* favicon is optional */
        }
      }
    } catch {
      /* branding is cosmetic; keep the last applied theme */
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      versionRef.current = null;
      setBranding(DEFAULTS);
      setLogoSrc(null);
      return undefined;
    }
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener(BRANDING_UPDATED_EVENT, onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener(BRANDING_UPDATED_EVENT, onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [userId, refresh]);

  useEffect(() => {
    let el = document.getElementById('tenant-branding-style');
    if (!el) {
      el = document.createElement('style');
      el.id = 'tenant-branding-style';
      document.head.appendChild(el);
    }
    el.textContent = buildBrandingCss(branding);
  }, [branding]);

  const formatDate = useCallback(
    (value, withTime = false) => {
      if (!value) return '';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: branding.timezone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(d).map((p) => [p.type, p.value]));
      const date = { 'YYYY-MM-DD': `${parts.year}-${parts.month}-${parts.day}`, 'MM/DD/YYYY': `${parts.month}/${parts.day}/${parts.year}`, 'DD/MM/YYYY': `${parts.day}/${parts.month}/${parts.year}` }[branding.dateFormat];
      return withTime ? `${date} ${parts.hour}:${parts.minute}` : date;
    },
    [branding.timezone, branding.dateFormat]
  );

  // Pages format amounts through utils/money.js, which has to know the currency before they render.
  setDisplayCurrency(branding.currency);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- formatAmount reads the currency set above
  const formatCurrency = useCallback((amount) => formatAmount(amount), [branding.currency]);

  const value = useMemo(() => ({ branding, logoSrc, formatDate, formatCurrency, refresh }), [branding, logoSrc, formatDate, formatCurrency, refresh]);
  return <TenantBrandingContext.Provider value={value}>{children}</TenantBrandingContext.Provider>;
}
